import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Search, Filter, Camera as CameraIcon, RefreshCw } from "lucide-react";
import { http } from "@/lib/api";
import CameraTile from "@/components/command/CameraTile";
import CameraFocusDialog from "@/components/command/CameraFocusDialog";

export default function Cameras() {
  const { alerts } = useOutletContext();
  const [cameras, setCameras] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [ptz, setPtz] = useState("all");
  const [focus, setFocus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await http.get("/cameras").then((x) => x.data);
      setCameras(r.cameras ?? []);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const alertByCam = useMemo(() => {
    const m = {};
    for (const a of alerts || []) {
      if (a.status === "resolved" || !a.camera_id) continue;
      if (!m[a.camera_id]) m[a.camera_id] = a;
    }
    return m;
  }, [alerts]);

  const filtered = useMemo(() => {
    return cameras.filter((c) => {
      if (status !== "all" && c.status !== status) return false;
      if (ptz === "ptz" && !c.ptz) return false;
      if (ptz === "fixed" && c.ptz) return false;
      if (q && !`${c.id} ${c.name} ${c.zone_id}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [cameras, q, status, ptz]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">Cameras</h1>
          <span className="micro-label hidden sm:inline">Registry · Live Feeds · PTZ</span>
        </div>
        <button onClick={load} className="btn-tac !px-2 !py-1.5" data-testid="cameras-refresh-btn" aria-label="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {/* Filter bar */}
        <div className="panel p-2 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search id / name / zone…"
              data-testid="cameras-search"
              className="w-full bg-transparent border border-border pl-7 pr-2 py-1 text-xs font-mono focus:outline-none focus:border-[hsl(var(--primary))]"
              style={{ borderRadius: 2 }}
            />
          </div>
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          {["all", "online", "degraded", "offline"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`cameras-status-${s}`}
              className={["px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                status === s ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground hover:text-foreground"].join(" ")}>
              {s}
            </button>
          ))}
          <span className="w-2" />
          {["all", "ptz", "fixed"].map((p) => (
            <button key={p} onClick={() => setPtz(p)} data-testid={`cameras-ptz-${p}`}
              className={["px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                ptz === p ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground hover:text-foreground"].join(" ")}>
              {p}
            </button>
          ))}
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">{filtered.length} / {cameras.length}</span>
        </div>

        {/* Grid */}
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }} data-testid="cameras-grid">
          {filtered.map((cam) => (
            <div key={cam.id} className="panel overflow-hidden" data-testid={`camera-card-${cam.id}`}>
              <div className="aspect-video">
                <CameraTile camera={cam} hasAlert={!!alertByCam[cam.id]} onExpand={setFocus} />
              </div>
              <div className="px-2 py-1.5 flex items-center gap-2 border-t border-border">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: cam.status === "online" ? "hsl(var(--success))" : "hsl(var(--warning))" }} />
                <span className="font-mono text-[11px] font-semibold tracking-tighter">{cam.id}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate flex-1">{cam.zone_id}</span>
                <span className="font-mono text-[9px] uppercase tracking-widest border border-border px-1">{cam.ptz ? "PTZ" : "FIX"}</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full p-6 text-center text-xs text-muted-foreground">
              <CameraIcon className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No cameras match your filters.
            </div>
          )}
        </div>
      </div>

      <CameraFocusDialog camera={focus} alerts={alerts || []} onClose={() => setFocus(null)} />
    </div>
  );
}
